import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import tradeDataStore from "@/store/tradeDataStore";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";

// LQC-2874: below 100% the API rounds each block's released size down to a whole
// multiple (PanelModelService.AddUpdateBlockSettings): 100,000 of notional, or 1 lot
// for futures. Anything under that is saved as 0 and the RP sees no size.
export const ICEBERG_MIN_RELEASE_NOTIONAL = 100000;
export const ICEBERG_MIN_RELEASE_LOTS = 1;

export interface IIcebergSizedBlock {
  notionalLeg1?: number | null;
  notionalLeg2?: number | null;
  instrumentType?: string;
}

const isFutureBlock = (block: IIcebergSizedBlock) =>
  block.instrumentType === InstrumentTypeCode.FUT;

function minReleaseForBlock(block: IIcebergSizedBlock): number {
  return isFutureBlock(block)
    ? ICEBERG_MIN_RELEASE_LOTS
    : ICEBERG_MIN_RELEASE_NOTIONAL;
}

// Sizes the API rounds for this block: leg 2 only on swaps. Futures are in lots.
function roundedNotionals(block: IIcebergSizedBlock): number[] {
  const notionals = [Number(block.notionalLeg1)];
  if (block.instrumentType === InstrumentTypeCode.SWP && block.notionalLeg2) {
    notionals.push(Number(block.notionalLeg2));
  }
  return notionals.filter((n) => Number.isFinite(n) && n > 0);
}

/** Blocks whose released size at `percent` (0-100) would fall under their minimum. */
export function findBlocksUnderIcebergMin<T extends IIcebergSizedBlock>(
  blocks: T[] | undefined,
  percent: number,
): T[] {
  if (!blocks?.length || !Number.isFinite(percent) || percent >= 100) return [];
  return blocks.filter((block) =>
    roundedNotionals(block).some(
      (notional) => (notional * percent) / 100 < minReleaseForBlock(block),
    ),
  );
}

/** Lowest whole iceberg % that keeps every block at or above its minimum. */
export function minIcebergPercentForBlocks(
  blocks: IIcebergSizedBlock[] | undefined,
): number {
  const percents = (blocks ?? []).flatMap((block) =>
    roundedNotionals(block).map((notional) =>
      Math.ceil((minReleaseForBlock(block) * 100) / notional),
    ),
  );
  if (!percents.length) return 0;
  return Math.min(Math.max(...percents), 100);
}

function describeMinRelease(blocks: IIcebergSizedBlock[]): string {
  const hasFuture = blocks.some(isFutureBlock);
  const hasOther = blocks.some((block) => !isFutureBlock(block));
  if (hasFuture && !hasOther) return "1 lot";
  if (hasFuture) return "100,000 (1 lot for futures)";
  return "100,000";
}

export function buildIcebergMinSizeMessage(
  blocks: IIcebergSizedBlock[] | undefined,
): string {
  const minPercent = minIcebergPercentForBlocks(blocks);
  return `Iceberg too small: each block must release at least ${describeMinRelease(blocks ?? [])}. Minimum for this trade is ${minPercent}%`;
}

/** The trade's blocks: the block store when loaded for this esid, else the trade's own legs. */
export function getIcebergBlocksForTrade(
  esid: string | undefined,
): IIcebergSizedBlock[] {
  if (!esid) return [];
  const blockData = tradeBlockDataStore
    .getState()
    .tradeBlockData.find((t) => t.esid === esid)?.blocks;
  if (blockData?.length) return blockData;
  const trade: any = tradeDataStore
    .getState()
    .tradeData.find((t: any) => t.esid === esid);
  return (
    trade?.executionRequestCounterParties?.[0]?.legs ??
    trade?.legs ??
    trade?.blocks ??
    []
  );
}
